import React, { useState, type FC, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { Input } from './input';
import { Button } from './button';
import { Activity } from 'lucide-react';

interface FooterProps extends React.HTMLAttributes<HTMLElement> {
    companyName?: string;
    description?: string;
    usefulLinks?: { label: string; href: string }[];
    socialLinks?: { label: string; href: string; icon: ReactNode }[];
    newsletterTitle?: string;
    onSubscribe?: (email: string) => Promise<boolean>;
}

export const Footer: FC<FooterProps> = ({
    companyName = 'Watchly',
    description = 'Real-time website monitoring that keeps your services running. Track uptime, performance, and receive instant alerts.',
    usefulLinks = [
        { label: 'Features', href: '#features' },
        { label: 'Pricing', href: '#pricing' },
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Privacy Policy', href: '#' },
    ],
    socialLinks = [
        { label: 'Twitter (X)', href: '#', icon: <TwitterIcon /> },
        { label: 'GitHub', href: '#', icon: <GitHubIcon /> },
        { label: 'LinkedIn', href: '#', icon: <LinkedInIcon /> },
    ],
    newsletterTitle = 'Subscribe to Updates',
    onSubscribe,
    className,
    ...props
}) => {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [subscriptionStatus, setSubscriptionStatus] = useState<'idle' | 'success' | 'error'>('idle');

    const handleSubscribe = async (event: React.FormEvent) => {
        event.preventDefault();
        if (!email || !onSubscribe || isSubmitting) return;
        setIsSubmitting(true);
        const success = await onSubscribe(email);
        setSubscriptionStatus(success ? 'success' : 'error');
        setIsSubmitting(false);
        if (success) setEmail('');
        setTimeout(() => setSubscriptionStatus('idle'), 3000);
    };

    return (
        <footer className={cn('bg-muted/30 border-t border-border text-foreground', className)} {...props}>
            <div className="container mx-auto grid grid-cols-1 gap-8 px-4 py-16 md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
                {/* Brand */}
                <div className="flex flex-col items-start gap-4">
                    <div className="flex items-center gap-2">
                        <Activity className="h-6 w-6 text-primary" />
                        <span className="text-xl font-bold">{companyName}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{description}</p>
                </div>

                {/* Useful Links */}
                <div className="md:justify-self-center">
                    <h3 className="mb-4 text-base font-semibold">Navigation</h3>
                    <ul className="space-y-2">
                        {usefulLinks.map((link) => (
                            <li key={link.label}>
                                <a href={link.href} className="text-sm text-muted-foreground transition-colors hover:text-primary">
                                    {link.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Follow Us */}
                <div className="md:justify-self-center">
                    <h3 className="mb-4 text-base font-semibold">Follow Us</h3>
                    <ul className="space-y-2">
                        {socialLinks.map((link) => (
                            <li key={link.label}>
                                <a href={link.href} aria-label={link.label} className="flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-primary">
                                    {link.icon}
                                    <span>{link.label}</span>
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Newsletter */}
                <div>
                    <h3 className="mb-4 text-base font-semibold">{newsletterTitle}</h3>
                    <form onSubmit={handleSubscribe} className="relative w-full max-w-sm">
                        <div className="relative flex gap-2">
                            <Input
                                type="email"
                                placeholder="Your email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={isSubmitting || subscriptionStatus !== 'idle'}
                                required
                                aria-label="Email for newsletter"
                            />
                            <Button type="submit" disabled={isSubmitting || subscriptionStatus !== 'idle'} size="sm">
                                {isSubmitting ? '...' : 'Join'}
                            </Button>
                        </div>
                        {(subscriptionStatus === 'success' || subscriptionStatus === 'error') && (
                            <div className="mt-2 text-sm font-medium">
                                {subscriptionStatus === 'success' ? (
                                    <span className="text-green-500">Subscribed! 🎉</span>
                                ) : (
                                    <span className="text-destructive">Failed. Please try again.</span>
                                )}
                            </div>
                        )}
                    </form>
                </div>
            </div>
            <div className="border-t border-border py-6 text-center text-sm text-muted-foreground">
                &copy; {new Date().getFullYear()} {companyName}. All rights reserved.
            </div>
        </footer>
    );
};

const TwitterIcon: FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
    </svg>
);
const GitHubIcon: FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.403 5.403 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" /><path d="M9 18c-4.51 2-5-2-7-2" />
    </svg>
);
const LinkedInIcon: FC = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect width="4" height="12" x="2" y="9" /><circle cx="4" cy="4" r="2" />
    </svg>
);
